import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { Subject } from 'rxjs';

import { WizardService } from '../wizard/wizard.service';
import { ErrorService } from '../error/error.service';

// TODO-COMMON
import { ApiService } from '../api/api.service';
// import { MixpanelService } from '@app/core/services/mixpanel/mixpanel.service';

import { DateRange, ErrorCategoryType, ISimpleContactSchema, IWizardAction } from 'common-lib';
import { IWizardResponse } from '../../models';
import { ERROR_MESSAGES } from '../error/error-messages.data';

import * as _ from 'lodash';

/* ------------------------------------------------------------------- */
/* -- This service exists to pass real-time data through the Wizard -- */
/* ---- INCOMING from parent to children and OUTGOING vice versa ----- */
/* ------------------------------------------------------------------- */
@Injectable()
export class EventService {
    private _onPageChange = new Subject();
    public onPageChange$ = this._onPageChange.asObservable();

    // For overriding the label in ActionBarComponent, e.g. choosing different options in Suggestions component
    private _onUpdateActionLabel = new Subject();
    public onUpdateActionLabel$ = this._onUpdateActionLabel.asObservable();

    private currentPage?: string;
    private prevPage = '';
    private pageHistory: string[] = [];

    public firstPage?: string;
    public actionOverride?: IWizardAction;
    public isNextDisabled = false;
    public isAddingContact = false;
    // this flag controls whether to restore the Wizard data to its previous state (the state right before the current one)
    public shouldResetOnBack = false;

    public isLoading: boolean;

    constructor(
        private apiService: ApiService,
        private wizardService: WizardService,
        private errorService: ErrorService
    ) {}

    /**
     * Called whenever the user interacts with Wizard Actions buttons
     * 1. Checks if user has permissions to make the call
     * 2. Capture any Mixpanel events if they exist
     * 3. Either changes the page (state) or makes an API call based on the action
     * Actions are dictated server-side in the Wizard JSON, but can sometimes be overridden
     * */
    public handleNextAction(action: IWizardAction) {
        const currentAction: IWizardAction | null = this.actionOverride ? this.actionOverride : action;

        if (!this.wizardService.taskMetaData.isAllowedApp) {
            return;
        }

        if (this.isAddingContact) {
            this.warnUnsavedChanges();
            return;
        }

        if (currentAction.nextPage) {
            this.handlePageChange('next', currentAction.nextPage);
        } else if (currentAction.api) {
            this.isLoading = true;
            this.handleApiCall(currentAction.api);
        } else {
            // TODO: Create Error Handler service for these
            console.error('Error: No next action defined!');
        }
        this.actionOverride = null;
    }

    public handlePageChange(action: 'next' | 'back', page: string | null) {
        switch (action) {
            case 'next':
                this.goNext(page);
                break;
            case 'back':
                this.goBack();
                break;
        }

        this.shouldResetOnBack = false;
    }

    /** APIs called by individual Wizard components */

    public requestDefaultOptions(payloadKeys: string[]) {
        return this.sendPostRequest('get-defaults', payloadKeys);
    }

    public requestDates(payload: { dateRange: DateRange; taskId?: string; type: string }) {
        const skill = this.wizardService.config.skill;
        return this.apiService.post(`wizard/${skill}/get-dates`, payload);
    }

    public postContact(contact: { name: string; email: string[]; defaultEmail: string }) {
        const contactToPost = _.isString(contact.email) ? { ...contact, email: [contact.email] } : contact;
        return this.apiService.post('contact', contactToPost);
    }

    /** @params contact is a stripped down version of IContactSchema */
    public updateContact(contact: ISimpleContactSchema, contactId: string) {
        return this.apiService.put('contact/', contactId, contact);
    }

    /** Helper method for when Wizard components need to replace default actions specified in the JSON */

    public overrideNextAction(label: string, action: { nextPage?: string; api?: any }) {
        this._onUpdateActionLabel.next(label);
        if (action.nextPage) {
            this.actionOverride = {
                label,
                nextPage: action.nextPage
            };
        } else if (action.api) {
            this.actionOverride = {
                label,
                api: action.api
            };
        }
    }

    /** The next three methods are generic helper methods for handling errors and resetting EventService */

    public clearErrorMessage(category: ErrorCategoryType, nextState?: { shouldStayDisabled: boolean }) {
        if (!this.errorService.currentError || this.errorService.currentError.category !== category) {
            return;
        }
        this.errorService.clearError(category);
        if (nextState && nextState.shouldStayDisabled === true) {
            return;
        }
        this.isNextDisabled = false;
    }

    private warnUnsavedChanges() {
        this.errorService.setError('wizard-unsaved-new-contact');
    }

    /** The next three methods handle navigating between the different states of a Wizard task */

    private goNext(page: string | null) {
        this._onPageChange.next(page);
        this.prevPage = this.currentPage || this.firstPage;
        this.currentPage = page;
        this.pageHistory = this.pageHistory.concat(this.prevPage);
        this.errorService.clearError();
    }

    private goBack() {
        this._onPageChange.next(this.prevPage);
        this.currentPage = this.pageHistory.pop();
        this.prevPage = this.pageHistory[this.pageHistory.length - 1] || this.firstPage;
        this.actionOverride = null;
        this.isNextDisabled = false;
        this.errorService.clearError();

        if (this.shouldResetOnBack) {
            this.wizardService.mergeCacheIntoOptions();
            this.wizardService.resetResponseData();
        }
    }

    /**
     * This will autonomously make the next action based on the instructions
     * sent in the (Wizard) response, hence it is not subscribed by any components
     */
    private handleApiCall(action: { payloadKeys?: string[]; endPoint: string }) {
        return this.sendPostRequest(action.endPoint, action.payloadKeys).subscribe(
            (response) => {
                const invalidResponse = this.checkIfDataIsInvalid(action.endPoint, response?.data);

                if (invalidResponse) {
                    this.errorService.setError(invalidResponse, {
                        content: this.dateTimeHelper(),
                        meta: response?.data.error?.meta
                    });
                    if (
                        [
                            'wizard-no-availability-specific-time',
                            'wizard-no-availability-specific-time-default'
                        ].indexOf(invalidResponse) > -1
                    ) {
                        // bookAnyhow is unset if the date or time has changed
                        this.wizardService.updateOptions({ bookAnyhow: true });
                    }
                    this.enableWizardActions();
                } else {
                    this.handleApiResponse(response);
                    this.errorService.clearError();
                }
            },
            (error) => {
                this.errorService.setServerError(error);
                this.enableWizardActions();
            }
        );
    }

    /**
     * The response will contain instructions on whether to make another API call
     * or to change the Wizard's current page view (state)
     */
    private handleApiResponse(response?: IWizardResponse) {
        if (response?.data) {
            this.wizardService.updateResponseData(response.data);
        }

        // Used for passing back values to the back end, e.g. handling PSL generation in meeting create
        if (response?.data && response?.shouldMergeDataIntoOptions) {
            this.wizardService.updateOptions(response.data);
        }

        if (response?.nextPage) {
            this.handlePageChange('next', response.nextPage);
            this.enableWizardActions();
        } else if (response?.api) {
            this.handleApiCall(response.api);
            return;
        }
    }

    private sendPostRequest(endPoint: string, payloadKeys: string[]) {
        const skill = this.wizardService.config.skill;
        const payload = payloadKeys.length ? this.wizardService.processOptionsForPayload(payloadKeys) : payloadKeys;

        return this.apiService.post(`wizard/${skill}/${endPoint}`, payload);
    }

    // TODO: This error message should ideally come from the server
    private checkIfDataIsInvalid(endPoint: string, data: any): keyof typeof ERROR_MESSAGES | false {
        if (endPoint.includes('check-availability')) {
            if (endPoint === 'check-availability' && data.error?.type) {
                return data.error.type;
            }
        }
        return false;
    }

    private dateTimeHelper() {
        const options = this.wizardService.options;
        const startDate = formatDate(options.dateStart.toDate(), 'MMMM d', 'en-US');
        if (options.isSpecificTime) {
            return `on ${startDate} at
        ${formatDate(options.timeStart.toDate(), 'h:mm a', 'en-US')}`;
        } else {
            const endDate = formatDate(options.dateEnd.toDate(), 'MMMM d', 'en-US');
            const onString = `on ${startDate}`;
            const betweenString = `between ${startDate} and ${endDate}`;
            return `${startDate === endDate ? onString : betweenString} for the times you have chosen`;
        }
    }

    private enableWizardActions() {
        this.isNextDisabled = this.isLoading = false;
    }
}
